import { Injectable } from '@angular/core';

import {
    fromEvent,
    Observable,
} from 'rxjs';
import {
    distinctUntilChanged,
    filter,
} from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class StorageService {
    public get keys(): Array<string> {
        const result: Array<string> = [];
        for (let i = 0; i < localStorage.length; i++) {
            result.push(localStorage.key(i)!);
        }
        return result;
    }

    public get isEmpty(): boolean {
        return this.keys.length === 0;
    }

    public remove(key: string | Array<string>) {
        if (typeof key === 'string') {
            localStorage.removeItem(key);
        } else if (key instanceof Array) {
            for (let i = 0; i < key.length; i++) {
                if (typeof key[i] === 'string') {
                    localStorage.removeItem(key[i]);
                } else {
                    throw new Error('Key in index ' + i + ' is not a string');
                }
            }
        } else {
            throw new Error('Key must be a string or array for function remove(key || array)');
        }
    }

    public get(key: string | null, defaultValue?: any) {
        if (typeof key !== 'string') {
            throw new Error('Key must be a string for function get(key)');
        }

        let value = localStorage.getItem(key)!;  // retrieve value
        const number = parseFloat(value); // to allow for number checking

        if (
            value === null ||
            value === 'null'
        ) {
            // Returns default value if key is not set, otherwise returns null
            return defaultValue != null ? defaultValue : null;
        }

        if (!isNaN(number)) {
            return number;  // value was of type number
        }

        if (value.toLowerCase() === 'true' || value.toLowerCase() === 'false') {
            return value === 'true';  // value was of type boolean
        }

        try {
            value = JSON.parse(value);
            return value;
        } catch (e) {
            return value;
        }
    }

    public set(key: string | null, value: any) {
        if (typeof key === 'string') {
            if (value == null) {
                this.remove(key);
                return;
            }

            if (typeof value === 'object') {
                value = JSON.stringify(value);
            }

            localStorage.setItem(key, value);
        } else {
            throw new Error('Invalid arguments for function set(key, value) or function set(object)');
        }
    }

    public store(value: any) {
        if (typeof value === 'object' && !(value instanceof Array)) {
            for (const property in value) {
                if (value.hasOwnProperty(property)) {
                    this.set(property, value[property]);
                }
            }
        } else {
            throw new Error('Argument for function set(object) must be an object');
        }
    }

    public contains(key: string) {
        if (typeof key !== 'string') {
            throw new Error('Key must be a string for function contains(key)');
        }

        return this.keys.indexOf(key) !== -1;
    }

    public clear() {
        localStorage.clear();
    }

    public watch(property: string): Observable<StorageEvent> {
        return fromEvent<StorageEvent>(window, 'storage').pipe(
            filter<StorageEvent>(e => e.key === property || !property),
            // IE spams localStorage events like crazy ;(
            distinctUntilChanged((a, b) => a.newValue === b.newValue),
        );
    }
}
